/**
 * Coordinates bulk actions: eligibility check → priority rules → execution.
 *
 * Three layers for separation of concerns (data validation, business logic, side effects).
 * Caches eligibility to avoid recomputing checks between display and execution.
 */
import { FC, useCallback, useRef } from 'react'

import {
  BulkActionsDefinition,
  FiltersDefinition,
  OnBulkActionCallback,
} from '@factorialco/f0-react/dist/experimental'
import * as Sentry from '@sentry/browser'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'
import { Section } from 'modules/expenses/lib/RouteContextParams'

import { getCommand } from './actionCommands'
import { determineAvailableActions } from './determineAvailableActions'
import { ActionEligibility, Expensable } from './types'
import { useActionEligibility } from './useActionEligibility'
import { useBulkActionModals } from './useBulkActionModals'

interface ItemStatus {
  item: Expensable
  checked: boolean
}

interface UseExpensesBulkActionsProps {
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  canSyncWithPayroll: boolean
  canSyncWithBanking: boolean
  canRestartApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  section?: Section
}

interface UseExpensesBulkActionsReturn {
  bulkActions: BulkActionsDefinition<Expensable, FiltersDefinition<string>>
  handleBulkAction: OnBulkActionCallback<Expensable, FiltersDefinition<string>>
  Modals: FC
  // Exposed for item actions integration
  handlers: {
    onApprove: (ids: number[], overrideApprovalFlow?: boolean) => Promise<unknown>
    onReject: (ids: number[], reason?: string, overrideApprovalFlow?: boolean) => Promise<unknown>
    onSetToPaid: (ids: number[]) => Promise<unknown>
    onRestartReview: (ids: number[]) => Promise<unknown>
    onRemoveFromPayroll: (ids: number[]) => Promise<unknown>
    onRevertPaid: (ids: number[]) => Promise<unknown>
  }
  modalActions: {
    openRejectModal: (expensables: Expensable[]) => void
    openPayrollModal: (expensables: Expensable[]) => void
    openPaymentModal: (expensables: Expensable[]) => void
  }
}

export const useExpensesBulkActions = ({
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  canSyncWithPayroll,
  canSyncWithBanking,
  canRestartApprovalFlow,
  canSetExpensableToPaid,
  section,
}: UseExpensesBulkActionsProps): UseExpensesBulkActionsReturn => {
  const {
    onApprove,
    onReject,
    onAddToPayroll,
    onAddToPayment,
    onRemoveFromPayroll,
    onRestartReview,
    onSetToPaid,
    onRevertPaid,
  } = useExpensableActions()

  const { computeEligibility } = useActionEligibility({
    canSyncWithPayroll,
    canSyncWithBanking,
    canRestartApprovalFlow,
    canSetExpensableToPaid,
    section,
  })

  const { openRejectModal, openPayrollModal, openPaymentModal, Modals } = useBulkActionModals({
    currentPayrollPeriodOpen,
    availablePayrollCategories,
    onReject,
    onAddToPayroll,
    onAddToPayment,
  })

  // Cache for eligibility computation to avoid double computation
  const eligibilityCache = useRef<{
    itemIds: string
    eligibility: ActionEligibility
  } | null>(null)

  /**
   * Computes eligibility with caching to prevent duplicate computations.
   * Memoizes based on selected item IDs to avoid recomputing when the same
   * items are processed multiple times (e.g., for display and execution).
   */
  const computeEligibilityWithCache = useCallback(
    (itemsStatus: readonly ItemStatus[]): ActionEligibility => {
      const selectedExpensables = itemsStatus
        .filter((item) => item.checked)
        .map((item) => item.item)

      // Create a stable key based on selected item IDs
      const itemIds = selectedExpensables
        .map((item) => item.id)
        .sort()
        .join(',')

      // Return cached result if available for the same items
      if (eligibilityCache.current && eligibilityCache.current.itemIds === itemIds) {
        return eligibilityCache.current.eligibility
      }

      // Compute eligibility for new selection
      const eligibility = computeEligibility(selectedExpensables)

      // Cache the result
      eligibilityCache.current = { itemIds, eligibility }

      return eligibility
    },
    [computeEligibility]
  )

  const bulkActions = useCallback<BulkActionsDefinition<Expensable, FiltersDefinition<string>>>(
    ({ itemsStatus }) => {
      const eligibility = computeEligibilityWithCache(itemsStatus)
      return determineAvailableActions(eligibility)
    },
    [computeEligibilityWithCache]
  )

  const handleBulkAction = useCallback<OnBulkActionCallback<Expensable, FiltersDefinition<string>>>(
    async (actionId, { itemsStatus }) => {
      const command = getCommand(actionId)
      if (!command) {
        return
      }

      const eligibility = computeEligibilityWithCache(itemsStatus)

      try {
        await command.execute(
          eligibility,
          {
            onApprove,
            onReject,
            onSetToPaid,
            onRestartReview,
            onRemoveFromPayroll,
            onRevertPaid,
          },
          {
            openRejectModal,
            openPayrollModal,
            openPaymentModal,
          }
        )
      } catch (error) {
        const selectedIds = itemsStatus.filter((item) => item.checked).map((item) => item.item.id)

        Sentry.captureException(error, {
          tags: { component: 'expenses-bulk-actions', actionId },
          extra: { selectedItemsCount: selectedIds.length, selectedIds, error },
        })
        // TODO: Replace with F0 Alert(?) when ready
        app.setFlash({
          type: 'wrong',
          message: i18n.t('expenses_v2.list.actions.action_failed'),
        })
      }
    },
    [
      computeEligibilityWithCache,
      onApprove,
      onReject,
      onRemoveFromPayroll,
      onRestartReview,
      onSetToPaid,
      onRevertPaid,
      openRejectModal,
      openPayrollModal,
      openPaymentModal,
    ]
  )

  return {
    bulkActions,
    handleBulkAction,
    Modals,
    // Expose handlers and modal actions for item actions
    handlers: {
      onApprove,
      onReject,
      onSetToPaid,
      onRestartReview,
      onRemoveFromPayroll,
      onRevertPaid,
    },
    modalActions: {
      openRejectModal,
      openPayrollModal,
      openPaymentModal,
    },
  }
}
